import * as React from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Checkbox,
  FormControlLabel,
  Dialog,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import ImageIcon from '@mui/icons-material/Image';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { CONST } from '../../utils';
import {
  ZPL_FIELD_OPTIONS, zplParse, zplParseBlock, zplGen, zplBlockSize, zplBlockLabel, zplBlankData
} from './zplDesigner';

const PALETTE_MIME = 'application/x-mes-zpl-palette';
const FIELD_GROUPS = ZPL_FIELD_OPTIONS.reduce((groups, field) => {
    let group = groups.find(item => item.name === field.group);
    if (!group){ group = { name: field.group, fields: [] }; groups.push(group); }
    group.fields.push(field);
    return groups;
}, []);

// Ancho de imagen: se edita local y se COMMITEA al salir del campo o con Enter —
// cada commit re-convierte la imagen en el servidor, no puede ser por tecla.
function ImageWidthField({ block, editable, busy, onCommit }) {
    const [val, setVal] = React.useState(Math.round(block.iw || 100));

    const commit = () => {
        const w = Math.max(8, +val || 100);
        setVal(w);
        if (w !== Math.round(block.iw || 100)){ onCommit(w); }
    };

    return (
        <TextField label='Ancho (dots)' type='number' size='small' value={val}
            disabled={!editable || busy}
            helperText={editable ? 'Enter o salir del campo para aplicar' : 'solo imágenes agregadas aquí'}
            onChange={(e) => setVal(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === 'Enter'){ commit(); } }} />
    );
}

// Diseñador visual de etiquetas ZPL — port del modal dz* del configurador de ARMENTO.
// Arrastra elementos sobre el PNG que renderiza el servidor; la reescritura es
// dirigida: los bloques no tocados se conservan byte-idénticos.
//
// Props:
//   open (bool), name (string), zpl (string inicial)
//   onApply(zplFinal), onClose()
//   apiPreview(zpl, dataOrNull) -> Promise<Blob|null>   (null = error)
//   apiImageToZpl(payload) -> Promise<{zpl,width,height}|null>
export default function LabelDesigner({ open, name, zpl, onApply, onClose, apiPreview, apiImageToZpl }) {

    const dzRef = React.useRef(null);
    const canvasWrapRef = React.useRef(null);
    const fileRef = React.useRef(null);
    const dragRef = React.useRef(null);

    const [tick, setTick] = React.useState(0);
    const [sel, setSel] = React.useState(null);
    const [bgUrl, setBgUrl] = React.useState('');
    const [status, setStatus] = React.useState('');
    const [showData, setShowData] = React.useState(true);
    const [paletteDragging, setPaletteDragging] = React.useState(false);
    const [busy, setBusy] = React.useState(false);

    const bump = () => setTick(t => t + 1);

    // (re)inicializa el estado al abrir
    React.useEffect(() => {
        if (!open){ return; }
        const source = (zpl && zpl.trim()) ? zpl : '^XA\n^PW812\n^LL406\n^XZ';
        const segs = zplParse(source);
        const lh = source.match(/\^LH(\d+),(\d+)/);
        dzRef.current = {
            segs: segs,
            W: 812, H: 300, scale: 0.7,
            lhx: lh ? +lh[1] : 0,
            lhy: lh ? +lh[2] : 0,
            refreshing: false, pending: false,
            gen: (dzRef.current ? dzRef.current.gen : 0) + 1,
            imgSrc: {}
        };
        setSel(null);
        setStatus('');
        setBgUrl('');
        bump();
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const dz = dzRef.current;
    const blocks = dz ? dz.segs.filter(s => s.kind === 'block') : [];

    // ── preview: latch de concurrencia (sin debounce por timer) ──
    const bgUrlRef = React.useRef('');

    const swapBgUrl = (url) => {
        const prev = bgUrlRef.current;
        bgUrlRef.current = url;
        setBgUrl(url);
        if (prev){ setTimeout(() => URL.revokeObjectURL(prev), 1500); }
    };

    // al desmontar / cerrar, liberar el último object URL
    React.useEffect(() => () => {
        if (bgUrlRef.current){ URL.revokeObjectURL(bgUrlRef.current); bgUrlRef.current = ''; }
    }, []);

    const refresh = async() => {
        const d = dzRef.current;
        if (!d){ return; }
        if (d.refreshing){ d.pending = true; return; }
        d.refreshing = true;
        const myGen = d.gen;

        try {
            const r = await apiPreview(zplGen(d.segs), showDataRef.current ? null : zplBlankData());

            if (myGen !== dzRef.current.gen){ return; }

            if (!r || !r.blob){
                setStatus((r && r.error) ? r.error : 'Error al renderizar la vista previa');
            } else {
                const url = URL.createObjectURL(r.blob);
                const img = new Image();
                await new Promise((res) => { img.onload = res; img.onerror = res; img.src = url; });
                if (myGen !== dzRef.current.gen){ URL.revokeObjectURL(url); return; }
                d.W = img.naturalWidth || 812;
                d.H = img.naturalHeight || 300;
                swapBgUrl(url);
                setStatus('');
                layout();
            }
        } catch (e) {
            setStatus(`Error de vista previa: ${e.message || e}`);
        } finally {
            d.refreshing = false;
        }
        if (d.pending){ d.pending = false; refresh(); }
    };

    // showData vive en un ref para que refresh (async) siempre lea el valor actual
    const showDataRef = React.useRef(true);
    React.useEffect(() => { showDataRef.current = showData; }, [showData]);

    const layout = () => {
        const d = dzRef.current;
        const wrap = canvasWrapRef.current;
        if (!d || !wrap){ return; }
        const maxW = Math.max(240, wrap.clientWidth - 44);
        d.scale = Math.max(0.3, Math.min(2, maxW / d.W));
        bump();
    };

    React.useEffect(() => {
        if (!open){ return; }
        const onResize = () => layout();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Posición de la capa en px: suma el offset ^LH y, para ^FT, sube una
    // altura de fuente (el texto crece desde la línea base).
    const elLeft = (b) => Math.round((b.x + dz.lhx) * dz.scale);
    const elTop = (b) => {
        const bl = b.baseline ? Math.round((b.fh || 24) * 0.81) : 0;
        return Math.round((b.y + dz.lhy - bl) * dz.scale);
    };

    // ── drag ──
    const startDrag = (ev, b) => {
        if (ev.target.dataset && ev.target.dataset.handle){ return; }
        ev.preventDefault();
        setSel(b);
        const d = dzRef.current;
        dragRef.current = { b, sx: ev.clientX, sy: ev.clientY, ox: b.x, oy: b.y, moved: false, mode: 'move' };
        const onMove = (e) => {
            const g = dragRef.current;
            if (!g){ return; }
            const dx = (e.clientX - g.sx) / d.scale;
            const dy = (e.clientY - g.sy) / d.scale;
            if (Math.abs(dx) > 1 || Math.abs(dy) > 1){ g.moved = true; }
            g.b.x = Math.max(0, Math.round(g.ox + dx));
            g.b.y = Math.max(0, Math.round(g.oy + dy));
            bump();
        };
        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            const g = dragRef.current;
            if (g && g.moved){ g.b.dirty = true; refresh(); }
            dragRef.current = null;
            bump();
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    const startResize = (ev, b) => {
        ev.preventDefault();
        ev.stopPropagation();
        setSel(b);
        const d = dzRef.current;
        dragRef.current = { b, sx: ev.clientX, sy: ev.clientY, ow: b.bw, oh: b.bh, mode: 'resize' };
        const onMove = (e) => {
            const g = dragRef.current;
            if (!g){ return; }
            g.b.bw = Math.max(2, Math.round(g.ow + (e.clientX - g.sx) / d.scale));
            g.b.bh = Math.max(2, Math.round(g.oh + (e.clientY - g.sy) / d.scale));
            bump();
        };
        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            if (dragRef.current){ dragRef.current.b.dirty = true; refresh(); }
            dragRef.current = null;
            bump();
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    // ── mutaciones desde el panel de propiedades ──
    const patchSel = (patch, relayout = false) => {
        if (!sel){ return; }
        Object.assign(sel, patch);
        sel.dirty = true;
        if (relayout){ layout(); }
        bump();
        refresh();
    };

    // ── alta / baja de elementos ──
    const insertBlock = (b) => {
        const d = dzRef.current;
        let idx = d.segs.length;
        for (let i = 0; i < d.segs.length; i++){
            if (d.segs[i].kind === 'raw' && /\^XZ/.test(d.segs[i].text)){ idx = i; break; }
        }
        d.segs.splice(idx, 0, b);
        setSel(b);
        bump();
        refresh();
    };

    const newId = () => 'n' + Math.round(performance.now() * 1000) + '_' + blocks.length;

    const addText = (field, position) => {
        const txt = field || 'Texto';
        const x = position ? position.x : 40;
        const y = position ? position.y : 40;
        insertBlock({ kind: 'block', id: newId(), raw: `\n^FO${x},${y}\n^A0N,28\n^FD${txt}\n^FS`,
            dirty: true, x, y, fh: 28, fw: null, type: field ? 'field' : 'text', text: txt, baseline: false });
    };

    const addBox = (position) => {
        const x = position ? position.x : 40;
        const y = position ? position.y : 40;
        insertBlock({ kind: 'block', id: newId(), raw: `\n^FO${x},${y}\n^GB120,60,3\n^FS`,
            dirty: true, x, y, type: 'box', bw: 120, bh: 60, bt: 3 });
    };

    const addQr = (position) => {
        const x = position ? position.x : 40;
        const y = position ? position.y : 40;
        insertBlock({ kind: 'block', id: newId(), raw: `\n^FO${x},${y}\n^BQN,2,4\n^FD {qr_code_data}\n^FS`,
            dirty: true, x, y, type: 'qr', mag: 4, qdata: ' {qr_code_data}' });
    };

    const addPaletteItem = (item, position) => {
        if (item.kind === 'field') addText(item.value, position);
        if (item.kind === 'text') addText(null, position);
        if (item.kind === 'box') addBox(position);
        if (item.kind === 'qr') addQr(position);
    };

    const startPaletteDrag = (event, item) => {
        event.dataTransfer.effectAllowed = 'copy';
        event.dataTransfer.setData(PALETTE_MIME, JSON.stringify(item));
        setPaletteDragging(true);
    };

    const dropPaletteItem = (event) => {
        const encoded = event.dataTransfer.getData(PALETTE_MIME);
        if (!encoded){ return; }
        event.preventDefault();
        const rect = event.currentTarget.getBoundingClientRect();
        const position = {
            x: Math.max(0, Math.round((event.clientX - rect.left) / scale - dz.lhx)),
            y: Math.max(0, Math.round((event.clientY - rect.top) / scale - dz.lhy))
        };
        try { addPaletteItem(JSON.parse(encoded), position); }
        catch (_) { setStatus('No fue posible agregar el elemento.'); }
        setPaletteDragging(false);
    };

    const addImage = async(file) => {
        setBusy(true);
        setStatus('Convirtiendo imagen…');
        const dataUrl = await new Promise((res, rej) => {
            const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(file);
        });
        const r = await apiImageToZpl({ image: dataUrl, x: 40, y: 40, width: 200, threshold: 128 });
        setBusy(false);
        if (!r){ setStatus('Error al convertir la imagen'); return; }
        const b = zplParseBlock('\n' + r.zpl, 0);
        b.id = newId();
        b.dirty = true;
        dzRef.current.imgSrc[b.id] = dataUrl;
        setStatus('');
        insertBlock(b);
    };

    const resizeImage = async(b, newW) => {
        const src = dzRef.current.imgSrc[b.id];
        if (!src || busy){ return; }
        setBusy(true);
        const r = await apiImageToZpl({ image: src, x: b.x, y: b.y, width: newW, threshold: 128 });
        setBusy(false);
        if (!r){ setStatus('Error al re-escalar la imagen'); return; }
        b.raw = '\n' + r.zpl;
        b.bpr = Math.ceil(r.width / 8);
        b.iw = r.width;
        b.ih = r.height;
        b.dirty = true;
        layout();
        refresh();
    };

    const deleteSel = () => {
        if (!sel){ return; }
        const d = dzRef.current;
        const i = d.segs.indexOf(sel);
        if (i >= 0){ d.segs.splice(i, 1); }
        if (d.imgSrc[sel.id]){ delete d.imgSrc[sel.id]; }
        setSel(null);
        bump();
        refresh();
    };

    if (!open || !dz){ return null; }

    const scale = dz.scale;

    return (
        <Dialog open={open} onClose={onClose} fullScreen>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
                {/* header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, padding: '10px 16px',
                           backgroundColor: CONST.Colors.Primary.Main, color: 'white' }}>
                    <Typography variant='h6'>🎨 Diseñador visual</Typography>
                    <Typography variant='body2' sx={{ opacity: 0.85, flexGrow: 1 }}>{name || '(sin nombre)'}</Typography>
                    <Button variant='contained' color='inherit' size='small' startIcon={<CheckIcon />}
                        sx={{ color: CONST.Colors.Primary.Main }}
                        onClick={() => onApply(zplGen(dz.segs))}>
                        Aplicar al código
                    </Button>
                    <IconButton color='inherit' onClick={onClose}><CloseIcon /></IconButton>
                </Box>

                <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0 }}>
                    {/* paleta */}
                    <Box sx={{ width: 230, flexShrink: 0, borderRight: '1px solid #dbe7f1', padding: 2, overflow: 'auto' }}>
                        <Typography variant='overline'>Data fields</Typography>
                        <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary', marginBottom: 1 }}>
                            Drag a field onto the label, or click to add it at the default position.
                        </Typography>
                        {FIELD_GROUPS.map(group => (
                            <Box key={group.name} sx={{ marginBottom: 1.5 }}>
                                <Typography variant='caption' sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', marginBottom: 0.5 }}>
                                    {group.name}
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
                                    {group.fields.map(field => {
                                        const item = { kind: 'field', value: field.value };
                                        return <Button key={field.value} draggable variant='outlined' size='small'
                                            onDragStart={(event) => startPaletteDrag(event, item)}
                                            onDragEnd={() => setPaletteDragging(false)} onClick={() => addPaletteItem(item)}
                                            sx={{ justifyContent: 'flex-start', minWidth: 0, textTransform: 'none', fontSize: 11, lineHeight: 1.2 }}>
                                            {field.label}
                                        </Button>;
                                    })}
                                </Box>
                            </Box>
                        ))}

                        <Typography variant='overline'>Elements</Typography>
                        {[
                            { kind: 'text', label: 'Free text' },
                            { kind: 'box', label: 'Box' },
                            { kind: 'qr', label: 'Protected QR' }
                        ].map(item => (
                            <Button key={item.kind} draggable fullWidth size='small' startIcon={<AddIcon />}
                                onDragStart={(event) => startPaletteDrag(event, item)}
                                onDragEnd={() => setPaletteDragging(false)} onClick={() => addPaletteItem(item)}
                                sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
                                {item.label}
                            </Button>
                        ))}
                        <Button fullWidth size='small' startIcon={<ImageIcon />} sx={{ justifyContent: 'flex-start' }}
                            disabled={busy} onClick={() => fileRef.current && fileRef.current.click()}>Imagen</Button>
                        <input type='file' hidden ref={fileRef} accept='image/*'
                            onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f){ addImage(f); } e.target.value = null; }} />

                        <Box sx={{ marginTop: 2 }}>
                            <Typography variant='overline'>Vista</Typography>
                            <FormControlLabel control={
                                <Checkbox size='small' checked={showData}
                                    onChange={(e) => { setShowData(e.target.checked); showDataRef.current = e.target.checked; refresh(); }} />
                            } label={<Typography variant='body2'>Datos de muestra</Typography>} />
                        </Box>
                        <Typography variant='caption' sx={{ color: 'gray', display: 'block', marginTop: 2 }}>
                            Drag fields to the exact position you want. Untouched ZPL remains byte-identical.
                        </Typography>
                    </Box>

                    {/* lienzo */}
                    <Box ref={canvasWrapRef} sx={{ flexGrow: 1, overflow: 'auto', padding: 3,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', backgroundColor: '#eef4fa' }}>
                        <Box sx={{ position: 'relative', backgroundColor: 'white', border: '1px solid #cbd8e6',
                                   boxShadow: 3, alignSelf: 'center',
                                   width: Math.round(dz.W * scale), height: Math.round(dz.H * scale), flexShrink: 0,
                                   outline: paletteDragging ? '3px solid #2f7fd0' : 'none', outlineOffset: 3 }}
                            onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = 'copy'; }}
                            onDrop={dropPaletteItem}
                            onMouseDown={(e) => { if (e.target === e.currentTarget || e.target.tagName === 'IMG'){ setSel(null); } }}>
                            {bgUrl && (
                                <img src={bgUrl} alt='preview' draggable={false}
                                    style={{ display: 'block', width: Math.round(dz.W * scale), height: Math.round(dz.H * scale),
                                             userSelect: 'none', pointerEvents: 'none' }} />
                            )}
                            <Box sx={{ position: 'absolute', inset: 0 }}>
                                {blocks.map(b => {
                                    const sz = zplBlockSize(b);
                                    const isSel = sel === b;
                                    return (
                                        <Box key={b.id}
                                            onMouseDown={(e) => startDrag(e, b)}
                                            sx={{
                                                position: 'absolute',
                                                left: elLeft(b), top: elTop(b),
                                                width: Math.max(8, Math.round(sz[0] * scale)),
                                                height: Math.max(8, Math.round(sz[1] * scale)),
                                                boxSizing: 'border-box',
                                                border: isSel ? '2px solid #1769c0' : '1px dashed rgba(11,59,84,.35)',
                                                backgroundColor: isSel ? 'rgba(80,150,220,.20)' : 'rgba(80,150,220,.05)',
                                                cursor: 'move',
                                                '&:hover': { borderColor: '#2f7fd0', backgroundColor: 'rgba(80,150,220,.14)' }
                                            }}>
                                            <Box sx={{ position: 'absolute', top: -16, left: -1, fontSize: 9.5, fontWeight: 800,
                                                       color: '#1769c0', backgroundColor: '#eaf3fc', border: '1px solid #bcd8f0',
                                                       borderRadius: '4px 4px 0 0', paddingX: 0.4, whiteSpace: 'nowrap',
                                                       pointerEvents: 'none', lineHeight: '15px', maxWidth: 180,
                                                       overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {zplBlockLabel(b)}
                                            </Box>
                                            {b.type === 'box' && (
                                                <Box data-handle='1'
                                                    onMouseDown={(e) => startResize(e, b)}
                                                    sx={{ position: 'absolute', width: 10, height: 10, right: -5, bottom: -5,
                                                          backgroundColor: '#1769c0', border: '1px solid white',
                                                          borderRadius: '2px', cursor: 'nwse-resize' }} />
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                        <Typography variant='caption' sx={{ marginTop: 1, color: status ? 'error.main' : 'gray' }}>
                            {status || `${dz.W}×${dz.H} dots @203dpi — escala ${scale.toFixed(2)}`}
                        </Typography>
                    </Box>

                    {/* propiedades */}
                    <Box sx={{ width: 260, flexShrink: 0, borderLeft: '1px solid #dbe7f1', padding: 2, overflow: 'auto' }}>
                        <Typography variant='overline'>Propiedades</Typography>
                        {!sel && (
                            <Typography variant='body2' sx={{ color: 'gray' }}>
                                Selecciona un elemento del lienzo.
                            </Typography>
                        )}
                        {sel && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Typography variant='body2' sx={{ fontFamily: 'monospace' }}>{zplBlockLabel(sel)}</Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <TextField label='X' type='number' size='small' value={Math.round(sel.x)}
                                        onChange={(e) => patchSel({ x: Math.max(0, +e.target.value || 0) }, true)} />
                                    <TextField label='Y' type='number' size='small' value={Math.round(sel.y)}
                                        onChange={(e) => patchSel({ y: Math.max(0, +e.target.value || 0) }, true)} />
                                </Box>
                                {sel.type === 'text' && (
                                    <TextField label='Text' size='small' value={sel.text || ''}
                                        onChange={(e) => patchSel({ text: e.target.value })} />
                                )}
                                {sel.type === 'field' && (
                                    <TextField label='Data field' size='small' value={sel.text || ''}
                                        InputProps={{ readOnly: true }} helperText='Controlled field: move or resize it without changing its data key.' />
                                )}
                                {(sel.type === 'text' || sel.type === 'field') && (
                                    <React.Fragment>
                                        <TextField label='Tamaño de letra' type='number' size='small' value={Math.round(sel.fh || 24)}
                                            onChange={(e) => patchSel({ fh: Math.max(6, +e.target.value || 24) }, true)} />
                                    </React.Fragment>
                                )}
                                {sel.type === 'box' && (
                                    <React.Fragment>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <TextField label='Ancho' type='number' size='small' value={Math.round(sel.bw || 20)}
                                                onChange={(e) => patchSel({ bw: Math.max(2, +e.target.value || 20) }, true)} />
                                            <TextField label='Alto' type='number' size='small' value={Math.round(sel.bh || 20)}
                                                onChange={(e) => patchSel({ bh: Math.max(2, +e.target.value || 20) }, true)} />
                                        </Box>
                                        <TextField label='Grosor' type='number' size='small' value={Math.round(sel.bt || 2)}
                                            onChange={(e) => patchSel({ bt: Math.max(1, +e.target.value || 2) })} />
                                    </React.Fragment>
                                )}
                                {(sel.type === 'qr' || sel.type === 'datamatrix') && (
                                    <TextField label={sel.type === 'qr' ? 'Datos QR' : 'Datos DataMatrix'} size='small'
                                        value={sel.qdata || ''} InputProps={{ readOnly: sel.type === 'qr' }}
                                        helperText={sel.type === 'qr' ? 'QR content is controlled by qr_code_data.' : ''}
                                        onChange={(e) => sel.type !== 'qr' && patchSel({ qdata: e.target.value })} />
                                )}
                                {sel.type === 'qr' && (
                                    <TextField label='Tamaño (mag)' type='number' size='small' value={Math.round(sel.mag || 4)}
                                        onChange={(e) => patchSel({ mag: Math.max(1, Math.min(10, +e.target.value || 4)) }, true)} />
                                )}
                                {sel.type === 'image' && (
                                    <ImageWidthField key={sel.id} block={sel} editable={!!dz.imgSrc[sel.id]}
                                        busy={busy} onCommit={(w) => resizeImage(sel, w)} />
                                )}
                                <Button color='error' variant='outlined' size='small' startIcon={<DeleteOutlineIcon />}
                                    onClick={deleteSel}>
                                    Borrar elemento
                                </Button>
                            </Box>
                        )}
                        {busy && <CircularProgress size={22} sx={{ marginTop: 2 }} />}
                    </Box>
                </Box>
            </Box>
        </Dialog>
    );
}
